import { Link } from 'react-router'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './count-detail-tooltip.css.ts'

/**
 * The things behind a count in a table, one hover away.
 *
 * Every count on this page's tables is a number a reader then has to go hunting for: "39 repositories are
 * below adequate on this dimension", "no control names this vulnerability in 17 repositories", "this
 * repository has 12 undefended vulnerabilities". The count is the actionable figure and the aggregate is
 * the only place that still knows what it is counting.
 *
 * **Capped, and it says so.** Thirty-nine names is not a tooltip. The same rule the least-defended table
 * follows: show the top of the list and state the size of what is not shown, rather than truncating in
 * silence.
 *
 * **Items link where there is somewhere to go.** A repository has its own page; a vulnerability class does
 * not, so those render as plain text. Radix keeps tooltip content hoverable, so a pointer can travel into
 * the card and click through — but tooltip content is not in the tab order, so links here are pointer-only.
 * The count itself stays focusable, and the same destinations are reachable from the tables.
 */

/** Enough to see the shape of the problem without the tooltip becoming the page. */
const VISIBLE_LIMIT = 8

export type CountDetailItem = {
  /** Stable across renders, so React does not reorder the list under the reader. */
  key: string
  name: string
  /** Where this item lives, when it has a page of its own. */
  href?: string
  /** A trailing figure, right-aligned — a score, a share. Omit where the name is the whole story. */
  value?: string
}

export const CountDetailTooltip: React.FC<{
  heading: string
  items: CountDetailItem[]
  /** What the items are, for the overflow line. */
  unit?: { one: string; many: string }
  /** Names the count for assistive tech, since the visible trigger is only a number. */
  triggerLabel: string
  children: React.ReactNode
}> = ({ heading, items, unit = { one: 'repository', many: 'repositories' }, triggerLabel, children }) => {
  // Nothing to name, so no affordance promising there is.
  if (items.length === 0) {
    return <>{children}</>
  }

  const shown = items.slice(0, VISIBLE_LIMIT)
  const hiddenCount = items.length - shown.length

  return (
    <TooltipWithHover
      trigger={
        <button type="button" className={styles.trigger} aria-label={triggerLabel}>
          {children}
        </button>
      }
      maxWidth={420}
      side="top"
      sideOffset={8}
      bare
    >
      <div className={styles.card}>
        <p className={styles.heading}>{heading}</p>
        <ul className={styles.list}>
          {shown.map(item => (
            <li key={item.key} className={styles.item}>
              {item.href === undefined ? (
                <span className={styles.plainName}>{item.name}</span>
              ) : (
                <Link className={styles.name} to={item.href}>
                  {item.name}
                </Link>
              )}
              {item.value !== undefined && <span className={styles.score}>{item.value}</span>}
            </li>
          ))}
        </ul>
        {hiddenCount > 0 && (
          <p className={styles.overflow}>{`+${hiddenCount} more ${pluralize(hiddenCount, unit.one, unit.many)}`}</p>
        )}
      </div>
    </TooltipWithHover>
  )
}
